import type { SVGProps } from 'react';

import { cn } from '@/lib/cn';

type IconProps = SVGProps<SVGSVGElement>;

/** Paw print — the site's decorative signature. */
export function PawIcon({ className, ...props }: IconProps) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="currentColor"
      className={cn('size-5', className)}
      aria-hidden="true"
      {...props}
    >
      <ellipse cx="6" cy="6.2" rx="2.1" ry="2.6" />
      <ellipse cx="10.6" cy="4.4" rx="1.9" ry="2.3" />
      <ellipse cx="15.2" cy="4.9" rx="1.9" ry="2.3" />
      <ellipse cx="18.6" cy="7.6" rx="1.9" ry="2.3" />
      <path d="M12 9.4c-3.6 0-7.2 2.6-5.7 7.2.5 1.6 2.1 3.6 5.7 3.6s5.2-2 5.7-3.6c1.5-4.6-2.1-7.2-5.7-7.2Z" />
    </svg>
  );
}

/** Official WhatsApp glyph. Only ever rendered on green WhatsApp affordances. */
export function WhatsAppIcon({ className, ...props }: IconProps) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="currentColor"
      className={cn('size-5', className)}
      aria-hidden="true"
      {...props}
    >
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347Z" />
      <path d="M12 0C5.373 0 0 5.373 0 12c0 2.123.558 4.112 1.528 5.838L.057 23.5l5.806-1.523A11.955 11.955 0 0 0 12 24c6.627 0 12-5.373 12-12S18.627 0 12 0Zm0 21.818a9.818 9.818 0 0 1-5.028-1.384l-.36-.214-3.745.982.999-3.648-.234-.375A9.818 9.818 0 1 1 12 21.818Z" />
    </svg>
  );
}

/**
 * Bull terrier do logo da BrucePets. Mesmo desenho do favicon.
 *
 * Orelhas eretas de ponta arredondada e vazadas, faixa clara descendo pelo
 * meio do rosto e focinho em coração. Sem esses três traços a silhueta escura
 * com pontas afiadas lê como uma figura demoníaca, não como um cachorro.
 *
 * As áreas claras usam `fill-cream-100` porque o ícone só aparece sobre creme
 * ou branco. Num fundo escuro elas precisariam acompanhar o fundo.
 */
export function BruceHeadIcon({ className, ...props }: IconProps) {
  const ear =
    'M20.6 7.4c-2 6.7-2.6 13.6-1.7 20.1l12-3.5c-2-7.2-5-13.4-7.6-16.8-.9-1.2-2.4-1.1-2.7.2Z';
  const earInner = 'M21.9 11.2c-1.5 5-2 10.3-1.4 15l7.9-2.3c-1.7-5.2-3.9-9.8-5.7-12.7Z';

  return (
    <svg
      viewBox="0 0 64 64"
      fill="none"
      className={cn('size-12', className)}
      aria-hidden="true"
      {...props}
    >
      <g fill="currentColor">
        <path d={ear} />
        <g transform="translate(64 0) scale(-1 1)">
          <path d={ear} />
        </g>
        <path d="M32 16c11 0 17 9.5 16 20.5-1.1 12-7.5 21.5-16 21.5s-14.9-9.5-16-21.5C15 25.5 21 16 32 16Z" />
      </g>

      <g className="fill-cream-100">
        <path d={earInner} />
        <g transform="translate(64 0) scale(-1 1)">
          <path d={earInner} />
        </g>
        <path d="M32 16.4c2.6 5 3.6 11 3.6 17s3 9.9 3 14.9c0 5.5-2.9 9.4-6.6 9.4s-6.6-3.9-6.6-9.4c0-5 3-8.9 3-14.9s1-12 3.6-17Z" />
        <path d="M20.8 34c.9-3 2.8-4.7 4.7-3.9.5 2.6-1 5.1-3.1 5.6-1 .2-1.8-.5-1.6-1.7Z" />
        <path d="M43.2 34c-.9-3-2.8-4.7-4.7-3.9-.5 2.6 1 5.1 3.1 5.6 1 .2 1.8-.5 1.6-1.7Z" />
      </g>

      <g fill="currentColor">
        <ellipse cx="23.2" cy="32.7" rx="1.1" ry="1.3" />
        <ellipse cx="40.8" cy="32.7" rx="1.1" ry="1.3" />
        <path d="M32 54.4c-2.6-2.1-4.8-3.6-4.8-5.9a2.5 2.5 0 0 1 4.8-1.2 2.5 2.5 0 0 1 4.8 1.2c0 2.3-2.2 3.8-4.8 5.9Z" />
      </g>
    </svg>
  );
}

/** Food bowl — used by the free-shipping progress meter. */
export function BowlIcon({ className, ...props }: IconProps) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="currentColor"
      className={cn('size-5', className)}
      aria-hidden="true"
      {...props}
    >
      <path d="M2.5 10.5h19a1 1 0 0 1 1 1.1 10.6 10.6 0 0 1-3.3 6.6l.4 1.8a1.2 1.2 0 0 1-1.2 1.5H5.6a1.2 1.2 0 0 1-1.2-1.5l.4-1.8a10.6 10.6 0 0 1-3.3-6.6 1 1 0 0 1 1-1.1Z" />
      <circle cx="8.2" cy="6.6" r="2.1" />
      <circle cx="13.4" cy="4.6" r="1.9" />
      <circle cx="17.8" cy="7.2" r="1.7" />
    </svg>
  );
}
